import {
  deriveActiveNavItem,
  derivePortalShellClass,
  type NavItemId,
  type NavSectionId,
  SEARCH_LABEL,
  SEARCH_PLACEHOLDER,
  SHELL_NAV_ITEMS,
  SHELL_NAV_SECTIONS,
  type ShellNavItemDefinition,
  WORKSPACE_NEW_SEGMENT,
} from "@lifter/hub-ui/browser-shared";
import {
  ChevronsUpDown,
  CircleUserRound,
  Command,
  HelpCircle,
  House,
  LifeBuoy,
  LogOut,
  Menu,
  MessageSquarePlus,
  Search,
  X,
} from "lucide-react";
import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutstandingApprovalCount } from "../api/approval-parcels";
import { useMeQuery } from "../api/me";
import { authClient } from "../auth/auth-client";
import { LOGIN_PATH } from "../auth/login-path";
import { BrandWordmark } from "../components/brand/BrandWordmark";
import { LifterWordmarkSpriteSource } from "../components/brand/lifter-wordmark-sprite";
import { clearUserDrafts } from "../components/chat/draft-store";
import { useShellPageIdentity } from "../components/editorial/PageIdentityContext";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button, ButtonLink } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { hasOpenOverlayLayers, Overlay } from "../components/ui/overlay";
import { RouterLink } from "../components/ui/router-link";
import { TooltipProvider } from "../components/ui/tooltip";
import { UserAvatar } from "../components/users/UserAvatar";
import { useTransitionMount } from "../hooks/useTransitionMount";
import { cn } from "../lib/cn";
import { consumeRouteFocusReset, hasPendingMainHeadingFocus } from "../lib/deferred-heading-focus";
import { usePortalOrg } from "../org/context";
import { RuntimeOfflineBanner } from "../pages/RuntimeOfflineBanner";
import {
  deriveMainScrollResetKey,
  useMainScrollRestoration,
} from "../routing/useMainScrollRestoration";
import { BrandStyleInjector } from "./BrandStyleInjector";
import { CommandPalette } from "./CommandPalette";
import { NavItem } from "./ShellNavItem";

interface PortalShellProps {
  children: ReactNode;
  activePathname: string;
  activeSearch?: string;
  onNavigate(path: string): void;
}

export function PortalShell({
  children,
  activePathname,
  activeSearch = "",
  onNavigate,
}: PortalShellProps) {
  const { current, isAdmin, canEditBranding, gatewayOnline } = usePortalOrg();
  const approvalCount = useOutstandingApprovalCount(gatewayOnline);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const activeNav = useMemo(() => deriveActiveNavItem(activePathname), [activePathname]);
  const shellClass = derivePortalShellClass(activePathname);
  const mainRef = useRef<HTMLElement | null>(null);
  useMainScrollRestoration(
    mainRef,
    shellClass === "page",
    shellClass === "page" ? deriveMainScrollResetKey(activePathname) : undefined,
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: route change is the trigger
  useEffect(() => {
    if (!consumeRouteFocusReset()) return;
    if (hasPendingMainHeadingFocus()) return;
    const main = mainRef.current;
    if (!main) return;
    const focusHeading = () => {
      if (main.contains(document.activeElement)) return true;
      const target = main.querySelector<HTMLElement>("h1");
      if (!target) return false;
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.dataset.routeFocusLanding = "true";
      target.focus({ preventScroll: true });
      return true;
    };
    if (focusHeading()) return;
    const observer = new MutationObserver(() => {
      if (focusHeading()) observer.disconnect();
    });
    observer.observe(main, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [activePathname, activeSearch]);
  // Non-admins must never see Administrator-only nav entries — no greyed-out
  // items, no 403 redirects. Organisation skills are deliberately available
  // to every authenticated member.
  // Per-agent edit affordances live inside the Agents detail page,
  // gated on the per-row `agent.viewerCanEdit` flag.
  const navItems = useMemo(
    () =>
      SHELL_NAV_ITEMS.filter((item) => {
        switch (item.requires) {
          case "admin":
            return isAdmin;
          case "branding":
            return canEditBranding;
          case "admin-or-branding":
            return isAdmin || canEditBranding;
          default:
            return true;
        }
      }),
    [canEditBranding, isAdmin],
  );
  // The rail carries daily work; the palette still reaches everything the
  // viewer may open, so hiding a row never hides a destination.
  const railItems = useMemo(() => navItems.filter((item) => !item.railHidden), [navItems]);
  const branding = current.org;

  // Close the mobile drawer whenever the route changes — the dependency on
  // activePathname is the whole point.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname change is the trigger
  useEffect(() => {
    setMenuOpen(false);
  }, [activePathname]);

  // Global ⌘K / Ctrl+K toggles the command palette. Bound on the document
  // so the top-bar `<kbd>` shortcut works regardless of where focus sits.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((open) => {
          // Don't stack the palette on top of an open modal/drawer/confirm —
          // navigating from underneath an in-progress flow discards its
          // state. (The open palette is itself a layer, so closing is fine.)
          if (!open && hasOpenOverlayLayers()) return open;
          return !open;
        });
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  const shell = (
    <div
      className={cn(
        "min-h-dvh bg-(--color-page-bg) text-(--color-text)",
        menuOpen && "max-lg:overflow-hidden max-lg:h-dvh",
      )}
    >
      <LifterWordmarkSpriteSource />
      <BrandStyleInjector branding={branding} />
      <a
        className="type-body sr-only focus:not-sr-only focus:fixed focus:left-6 focus:top-6 focus:z-50 focus:rounded-(--radius-md) focus:border focus:border-(--color-border-strong) focus:bg-(--color-surface) focus:px-4 focus:py-2"
        href="#main-content"
      >
        Skip to content
      </a>

      <div className="grid min-h-dvh grid-cols-1 lg:grid-cols-[var(--layout-sidebar)_minmax(0,1fr)]">
        {/* Sidebar pinned to viewport height; only the middle Navigation
            scrolls. Keeps the user button (in SidebarFooter) on screen
            even when the main content is multiple pages long. */}
        {/* Chrome sits on --color-surface and the canvas keeps --color-page-bg,
            so the rail and the top bar read as one continuous frame around the
            content rather than the rail dissolving into it. */}
        <aside className="hidden border-r border-(--color-border) bg-(--color-surface) lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col">
          <SidebarHeader branding={branding} />
          <Navigation activeNav={activeNav} items={railItems} approvalCount={approvalCount} />
          <SidebarFooter />
        </aside>

        <div className="flex min-w-0 flex-col h-dvh overflow-hidden">
          <DesktopTopbar branding={branding} onOpenSearch={openSearch} />
          <MobileHeader
            branding={branding}
            onOpenMenu={() => setMenuOpen(true)}
            onOpenSearch={openSearch}
          />
          {/* While the mobile drawer is open it is the only interactive
              surface; `inert` pulls the page content out of the tab order and
              the accessibility tree so keyboard and SR users can't reach behind
              the modal. React drops the attribute when `menuOpen` flips false. */}
          {/* The skip link needs a programmatic landmark target outside the
              sequential tab order. Route focus handoffs wait for the page h1
              and never use the landmark as a fallback. */}
          {/* `relative` makes this the containing block for the page's
              absolutely positioned content, Tailwind's `sr-only` included.
              Such content otherwise escapes this scroller's clip, lands in the
              initial containing block, and gives the document the scrollable
              overflow the `h-dvh overflow-hidden` column above rules out, so a
              fling at a page's bottom bound chains into the whole shell. */}
          <main
            className={cn(
              "relative min-h-0 flex-1",
              shellClass === "workbench"
                ? "flex flex-col overflow-hidden"
                : "overflow-y-auto px-(--layout-page-x) py-(--layout-page-y)",
            )}
            id="main-content"
            inert={menuOpen}
            ref={mainRef}
            tabIndex={-1}
          >
            <div
              className={cn(
                "w-full",
                shellClass === "workbench"
                  ? "flex min-h-0 flex-1 flex-col"
                  : "mx-auto flex min-h-full flex-col",
              )}
              style={shellClass === "page" ? { maxWidth: "var(--layout-content-max)" } : undefined}
            >
              <RuntimeOfflineBanner />
              {children}
            </div>
          </main>
        </div>
      </div>

      <MobileDrawer
        approvalCount={approvalCount}
        activeNav={activeNav}
        branding={branding}
        items={railItems}
        onClose={() => setMenuOpen(false)}
        open={menuOpen}
      />

      <CommandPalette
        navItems={navItems}
        onClose={closeSearch}
        onNavigate={onNavigate}
        open={searchOpen}
      />
    </div>
  );

  return <TooltipProvider>{shell}</TooltipProvider>;
}

function SidebarHeader({
  branding,
}: {
  branding: { slug: string; displayName: string | null; logoUrl: string | null };
}) {
  // Same height as the top bar so the two bottom borders form one unbroken rule
  // across the head of the app.
  return (
    <div className="flex h-(--layout-header-h) shrink-0 items-center border-b border-(--color-border) px-(--space-3)">
      <BrandWordmark branding={branding} size="sidebar" />
    </div>
  );
}

interface NavigationProps {
  approvalCount?: number;
  activeNav: NavItemId | null;
  items: readonly ShellNavItemDefinition[];
  onSelect?: () => void;
}

function Navigation({ activeNav, items, onSelect, approvalCount }: NavigationProps) {
  const sectionGroups = useMemo(() => {
    const groups: { section: NavSectionId; label?: string; items: ShellNavItemDefinition[] }[] = [];
    for (const section of SHELL_NAV_SECTIONS) {
      const sectionItems = items.filter((item) => item.section === section.id);
      if (sectionItems.length > 0) {
        groups.push({ section: section.id, label: section.label, items: sectionItems });
      }
    }
    return groups;
  }, [items]);

  return (
    // The fade sits over the nav's bottom edge as a scroll cue. It gradients
    // from the chrome surface to transparent, so it is invisible when the list
    // fits and only reads as an edge once rows pass beneath it — no overflow
    // measurement, and nothing to keep in sync.
    <div className="relative flex min-h-0 flex-1 flex-col">
      {/* `overscroll-contain` keeps a fling at the nav's scroll bounds from
          chaining outward — into the page for the desktop sidebar, and into the
          (locked) document when this renders inside the mobile drawer. */}
      <nav
        aria-label="Primary"
        className="flex min-h-0 flex-1 flex-col gap-(--space-5) overflow-y-auto overscroll-contain px-(--space-3) py-(--space-4)"
      >
        {sectionGroups.map((group) => (
          <div key={group.section}>
            {group.label ? (
              <div className="type-eyebrow px-(--space-3) pb-(--space-1) text-(--color-text-tertiary)">
                {group.label}
              </div>
            ) : null}
            <ul className="flex flex-col gap-px">
              {group.items.map((item) => (
                <NavItem
                  activeNav={activeNav}
                  item={item}
                  key={item.id}
                  onSelect={onSelect}
                  count={item.id === "approvals" ? approvalCount : undefined}
                />
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-(--space-6) [background-image:linear-gradient(to_top,var(--color-surface),transparent)]"
      />
    </div>
  );
}

function SidebarFooter() {
  return (
    <div className="mt-auto shrink-0 border-t border-(--color-border) p-(--space-2)">
      <UserMenu wide />
    </div>
  );
}

interface TopbarProps {
  branding: { slug: string; displayName: string | null; logoUrl: string | null };
  onOpenSearch(): void;
}

export function DesktopTopbar({ branding, onOpenSearch }: TopbarProps) {
  const { breadcrumbs } = useShellPageIdentity();
  const tenantName = branding.displayName ?? "Workspace";
  // The page heading names the detail; the top bar offers the way back to its section.
  const shownBreadcrumbs = breadcrumbs.slice(0, 1);

  // Horizontal padding matches the page rail so the breadcrumb's left edge lines up
  // with the page heading beneath it.
  return (
    <header className="sticky top-0 z-30 hidden h-(--layout-header-h) shrink-0 items-center grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] gap-(--space-6) border-b border-(--color-border) bg-(--color-surface) px-(--layout-page-x) lg:grid">
      <Breadcrumb aria-label="Breadcrumb" className="type-body min-w-0">
        <BreadcrumbList className="flex-nowrap">
          {/* The sidebar header already names the tenant, so the row's first and
              most valuable position carries only the way back to the root. The
              tenant name is still the icon's accessible name: this is that
              tenant's root, not a generic "Home". */}
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <RouterLink
                aria-label={tenantName}
                className="inline-flex items-center focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                href="/"
              >
                <House aria-hidden="true" className="size-(--space-4)" />
              </RouterLink>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {shownBreadcrumbs.map((crumb, index) => {
            // Only the real tail of the trail is the page you are on. A trail
            // that was trimmed ends somewhere else, so its last visible crumb
            // stays a link rather than becoming inert page text.
            const current =
              breadcrumbs.length === shownBreadcrumbs.length &&
              index === shownBreadcrumbs.length - 1;
            return (
              <Fragment key={crumb.href}>
                <BreadcrumbSeparator />
                <BreadcrumbItem className="min-w-0">
                  {current ? (
                    <BreadcrumbPage className="truncate">{crumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink
                      asChild
                      className="truncate"
                      style={{
                        color: "var(--color-text)",
                        fontWeight: "var(--type-weight-semibold)",
                      }}
                    >
                      <RouterLink href={crumb.href}>{crumb.label}</RouterLink>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <SearchTrigger onOpen={onOpenSearch} />
      <div className="flex items-center gap-(--space-2)">
        <NewChatButton />
        <HelpMenu onOpenSearch={onOpenSearch} />
      </div>
    </header>
  );
}

/**
 * The top-bar search field is a *button* that opens the command palette, not a
 * live text input — the real query happens inside the palette where results,
 * keyboard nav, and grouping live. Styled to read as the familiar search box so
 * the affordance is obvious. ⌘K opens the same palette globally.
 */
const IS_APPLE_PLATFORM =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const SEARCH_SHORTCUT_HINT = IS_APPLE_PLATFORM ? "⌘K" : "Ctrl K";

function SearchTrigger({ onOpen }: { onOpen(): void }) {
  return (
    <button
      aria-haspopup="dialog"
      aria-keyshortcuts="Meta+K Control+K"
      aria-label={SEARCH_LABEL}
      className="type-label cursor-pointer hidden h-(--field-h) max-w-md flex-1 items-center gap-(--space-2) rounded-(--radius-md) border border-(--color-border) bg-(--color-page-bg) px-(--space-3) [transition:border-color_var(--motion-fast),background-color_var(--motion-fast),color_var(--motion-fast)] hover:border-(--color-border-strong) hover:bg-(--color-surface-2) focus-visible:border-(--color-border-strong) lg:flex"
      onClick={onOpen}
      type="button"
    >
      <Search aria-hidden="true" className="size-4 text-(--color-text-faint)" strokeWidth={1.6} />
      <span className="flex-1 truncate text-left text-(--color-text-faint)">
        {SEARCH_PLACEHOLDER}
      </span>
      <kbd className="type-micro rounded-(--radius-sm) border border-(--color-border) bg-(--color-surface) px-1.5 py-0.5 text-(--color-text-faint)">
        {SEARCH_SHORTCUT_HINT}
      </kbd>
    </button>
  );
}

/**
 * Starting a conversation is the product's most common intent, and the Workspace
 * itself cannot offer it: bare `/workspace` opens the newest thread, so the entry
 * composer is unreachable from inside a conversation. Putting it in the top bar
 * makes it reachable from every page, which is also where a reader looks for it.
 */
function NewChatButton() {
  return (
    <ButtonLink
      className="hidden lg:inline-flex"
      href={`/workspace/${WORKSPACE_NEW_SEGMENT}`}
      size="sm"
      variant="secondary"
    >
      <MessageSquarePlus aria-hidden="true" className="size-(--space-3)" />
      New chat
    </ButtonLink>
  );
}

function HelpMenu({ onOpenSearch }: { onOpenSearch(): void }) {
  const { current } = usePortalOrg();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="cursor-pointer size-9 justify-center !border-transparent !bg-transparent px-0 text-(--color-text-secondary) hover:!bg-(--color-surface-2) hover:text-(--color-text)">
        <HelpCircle aria-hidden="true" className="size-5 min-w-5" strokeWidth={1.7} />
        <span className="sr-only">Help</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {current.hub?.supportEmail ? (
          <DropdownMenuItem href="/account#support">
            <LifeBuoy aria-hidden="true" className="size-4 shrink-0 opacity-80" />
            Contact support
          </DropdownMenuItem>
        ) : null}
        {/* Surfaces the ⌘K hint as a real action (opens the palette) rather than
            a dead "keyboard shortcuts" link pointing nowhere. */}
        <DropdownMenuItem onSelect={onOpenSearch}>
          <Command aria-hidden="true" className="size-4 shrink-0 opacity-80" />
          <span className="flex-1">Search</span>
          <kbd className="type-micro rounded-(--radius-sm) border border-(--color-border) bg-(--color-page-bg) px-1.5 py-0.5 text-(--color-text-faint)">
            {SEARCH_SHORTCUT_HINT}
          </kbd>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileHeader({
  branding,
  onOpenMenu,
  onOpenSearch,
}: {
  branding: { slug: string; displayName: string | null; logoUrl: string | null };
  onOpenMenu: () => void;
  onOpenSearch: () => void;
}) {
  return (
    <header className="sticky top-0 z-30 flex h-(--layout-header-h) shrink-0 items-center justify-between border-b border-(--color-border) bg-(--color-surface) px-(--space-4) lg:hidden">
      <div className="flex items-center gap-3">
        <Button aria-label="Open menu" iconOnly onClick={onOpenMenu} size="md" variant="ghost">
          <Menu aria-hidden="true" className="size-5" />
        </Button>
        <BrandWordmark branding={branding} size="topbar" />
      </div>
      <div className="flex items-center gap-1">
        {/* The desktop top bar carries the search field; on mobile this is the
            only entry point to the command palette (there is no ⌘K on touch). */}
        <Button aria-label="Search" iconOnly onClick={onOpenSearch} size="md" variant="ghost">
          <Search aria-hidden="true" className="size-5" />
        </Button>
        <UserMenu />
      </div>
    </header>
  );
}

function MobileDrawer({
  approvalCount,
  activeNav,
  branding,
  items,
  onClose,
  open,
}: {
  approvalCount?: number;
  activeNav: NavItemId | null;
  branding: { slug: string; displayName: string | null; logoUrl: string | null };
  items: readonly ShellNavItemDefinition[];
  onClose: () => void;
  open: boolean;
}) {
  const drawerRef = useRef<HTMLElement | null>(null);
  const { mounted, active, unmount } = useTransitionMount(open, drawerRef);

  if (!mounted) return null;

  return (
    <Overlay
      ariaLabel="Navigation menu"
      as="aside"
      backdropClassName={cn(
        "absolute inset-0 bg-(--color-overlay) [transition:opacity_var(--motion-base)]",
        active ? "opacity-100" : "opacity-0",
      )}
      backdropLabel="Close menu backdrop"
      onClose={onClose}
      onTransitionEnd={(event) => {
        if (event.target === event.currentTarget && !open) unmount();
      }}
      open={open}
      present={mounted}
      rootClassName={cn("fixed inset-0 z-100 lg:hidden", !active && "pointer-events-none")}
      // Wider than the desktop rail: at phone widths the rail's 240px forces
      // longer nav labels to truncate, and the extra room is free here.
      surfaceClassName={cn(
        "absolute inset-y-0 left-0 flex w-[min(calc(var(--layout-sidebar)+var(--space-16)),86vw)] flex-col bg-(--color-surface) shadow-(--shadow-modal) outline-none [transition:translate_var(--motion-base)]",
        active ? "translate-x-0" : "-translate-x-full",
      )}
      surfaceRef={drawerRef}
    >
      <div className="flex h-(--layout-header-h) shrink-0 items-center justify-between border-b border-(--color-border) px-(--space-3)">
        <BrandWordmark branding={branding} size="sidebar" />
        <Button
          aria-label="Close menu"
          data-overlay-close="true"
          iconOnly
          onClick={onClose}
          variant="ghost"
        >
          <X aria-hidden="true" className="size-5" />
        </Button>
      </div>

      <Navigation
        activeNav={activeNav}
        items={items}
        onSelect={onClose}
        approvalCount={approvalCount}
      />
      <SidebarFooter />
    </Overlay>
  );
}

/**
 * Identity chrome, one dropdown in two presentations: `wide` fills the rail
 * footer with avatar, name and email; the compact form is an avatar-only
 * trigger for the mobile header. Both open the same menu, so account and
 * sign-out have exactly one home per viewport.
 *
 * Sign-out is a menu item rather than persistent chrome: it ends the session,
 * which is rare and worth one deliberate extra click. It revokes server-side
 * and hard-navigates to `/login`, and the full reload guarantees no
 * authenticated cache or component state survives into the signed-out page.
 */
function UserMenu({ wide = false }: { wide?: boolean }) {
  const me = useMeQuery();
  const displayName = me.data?.displayName?.trim() || me.data?.email || "Account";
  const email = me.data?.email ?? "";
  const showEmail = Boolean(email) && email !== displayName;

  async function signOut() {
    if (me.data?.userId) clearUserDrafts(me.data.userId);
    await authClient.signOut().catch(() => undefined);
    window.location.assign(LOGIN_PATH);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "cursor-pointer !border-transparent !bg-transparent text-(--color-text-secondary) hover:!bg-(--color-surface-2) hover:text-(--color-text)",
          wide
            ? "!h-auto w-full gap-(--space-2) px-(--space-2) py-(--space-2)"
            : "size-9 justify-center px-0",
        )}
      >
        <UserAvatar name={displayName} size={wide ? "md" : "sm"} tooltip={false} />
        {wide ? (
          <>
            <span className="min-w-0 flex-1">
              <span className="type-body-strong block truncate text-(--color-text)">
                {displayName}
              </span>
              {showEmail ? (
                <span className="type-meta block truncate text-(--color-text-tertiary)">
                  {email}
                </span>
              ) : null}
            </span>
            <ChevronsUpDown
              aria-hidden="true"
              className="size-4 shrink-0 text-(--color-text-tertiary)"
              strokeWidth={1.6}
            />
          </>
        ) : null}
        <span className="sr-only">Account menu</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={wide ? "start" : "end"}>
        {/* Names the session the menu acts on. Not a control: the account link
            below is the thing to click. */}
        <div className="mb-(--space-1) min-w-0 border-b border-(--color-border) px-(--space-3) pb-(--space-2)">
          <span className="type-body-strong block truncate text-(--color-text)">{displayName}</span>
          {showEmail ? (
            <span className="type-meta block truncate text-(--color-text-tertiary)">{email}</span>
          ) : null}
        </div>
        <DropdownMenuItem href="/account">
          <CircleUserRound aria-hidden="true" className="size-4 shrink-0 opacity-80" />
          Your account
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut aria-hidden="true" className="size-4 shrink-0 opacity-80" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
